/**
 * Phone number utilities for validation and formatting
 */

const NON_ENGLISH_DIGITS_PATTERN = /[٠-٩۰-۹]/;
const MIN_PHONE_DIGITS = 10;
const MAX_PHONE_DIGITS = 15;

/**
 * Check if phone contains non-English (Arabic/Persian) digits
 * @param {string} phone - Phone number to check
 * @returns {boolean} - True if contains non-English digits
 */
export const containsNonEnglishDigits = (phone) => {
  if (!phone) return false;
  return NON_ENGLISH_DIGITS_PATTERN.test(phone);
};

/**
 * Validate phone number
 * @param {string} phone - Phone number to validate
 * @param {Function} t - Translation function
 * @param {Object} options - Validation options
 * @param {boolean} options.required - Whether phone is required (default: false)
 * @returns {string|null} - Error message or null if valid
 */
export const validatePhone = (phone, t, options = {}) => {
  const { required = false } = options;

  if (!phone) {
    return required ? t('profileData.phoneRequired') : null;
  }
  if (containsNonEnglishDigits(phone)) {
    return t('profileData.englishNumbersOnly');
  }
  const digitsOnly = phone.replace(/\D/g, '');
  if (
    digitsOnly.length < MIN_PHONE_DIGITS ||
    digitsOnly.length > MAX_PHONE_DIGITS
  ) {
    return t('profileData.invalidPhoneLength');
  }
  return null;
};

/**
 * Format phone for display (ensure + prefix)
 * @param {string} phone - Phone number from database
 * @returns {string} - Formatted phone with + prefix
 */
export const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  return `+${phone}`;
};

/**
 * Format phone for storage (remove + prefix)
 * @param {string} phone - Phone number with potential + prefix
 * @returns {string|null} - Phone without + prefix or null
 */
export const formatPhoneForStorage = (phone) => {
  if (!phone) return null;
  return phone.replace(/^\+/, '');
};
